import { TextField } from "@mui/material";
import { useState } from "react";
import clsx from "clsx";
import Text from "../../styleguide/Text";
import {
  DateTimePicker as MuiDateTimePicker,
  DateTimePickerProps,
} from "@mui/x-date-pickers/DateTimePicker";
import moment, { Moment } from "moment";

interface DatePicker {
  label?: string;
  placeholder?: string;
  handleChangeDate?: (newValue: Date | null) => void;
  value?: Date | string | null;
  disabled?: boolean;
  className?: string;
  withVerticalSpacer?: boolean;
  isRequired?: boolean;
  touched?: boolean;
  error?: string;
  textInputClassName?: string;
  dateFormat?: string;
  fullWith?: boolean;
  gutterBottom?: boolean;
  maxDate?: Date;
  minDate?: Date;
  withClearData?: boolean;
  currentTimezone?: string;
  withAmPm?: boolean;
  defaultTimeToNow?: boolean;
  size?: any;
}

const DateTimePicker = ({
  label,
  placeholder,
  handleChangeDate,
  value,
  disabled,
  className,
  withVerticalSpacer,
  isRequired,
  textInputClassName,
  dateFormat = "DD/MM/YYYY HH:mm",
  fullWith = true,
  gutterBottom = true,
  maxDate,
  minDate,
  withClearData,
  touched,
  error,
  size = "medium",
  withAmPm = false,
  defaultTimeToNow = false,
}: DatePicker) => {
  const [activeView, setActiveView] =
    useState<NonNullable<DateTimePickerProps<Moment, Moment>["openTo"]>>("day");
  const parsedValue = value ? moment(value) : null;
  const parsedMinDate = minDate ? moment(minDate) : undefined;
  const parsedMaxDate = maxDate ? moment(maxDate) : undefined;

  const handleChange = (newValue: Moment | null) => {
    let nextValue = newValue?.clone() ?? null;
    if (defaultTimeToNow && activeView === "day" && nextValue?.isValid()) {
      const now = moment();
      nextValue.set({
        hour: now.hour(),
        minute: now.minute(),
        second: 0,
        millisecond: 0,
      });
      if (
        parsedMinDate?.isValid() &&
        nextValue.isSame(parsedMinDate, "day") &&
        nextValue.isBefore(parsedMinDate)
      ) {
        nextValue = parsedMinDate.clone();
      }
    }
    if (withClearData || nextValue) {
      handleChangeDate?.(nextValue?.toDate() ?? null);
    }
  };

  return (
    <div
      className={`${fullWith ? "w-full" : ""} ${
        withVerticalSpacer ? "px-1" : ""
      } ${className}`}>
      <MuiDateTimePicker<Moment>
        componentsProps={{
          actionBar: {
            actions: withClearData ? ["clear"] : [],
          },
        }}
        inputFormat={dateFormat}
        value={parsedValue || null}
        disableMaskedInput
        onChange={handleChange}
        onViewChange={defaultTimeToNow ? setActiveView : undefined}
        onOpen={defaultTimeToNow ? () => setActiveView("day") : undefined}
        disabled={disabled}
        minDateTime={parsedMinDate}
        maxDateTime={parsedMaxDate}
        ampm={withAmPm ? true : false}
        className={clsx(
          "mb-3 w-full rounded-control bg-surface-subtle [&_.MuiOutlinedInput-root]:h-[48px] [&_.MuiOutlinedInput-root]:rounded-control [&_.MuiOutlinedInput-root]:bg-surface-subtle [&_.MuiOutlinedInput-input]:py-0",
          textInputClassName,
        )}
        renderInput={(params) => (
          <div className={`flex flex-col ${gutterBottom ? "mb-7" : ""}`}>
            {label && (
              <label className="mb-2 flex items-center font-medium text-slate-900">
                {label}
                {isRequired && <span className="ml-1 text-danger">*</span>}
              </label>
            )}
            <TextField
              sx={{
                "& fieldset": { border: "none" },
              }}
              size={size}
              variant="outlined"
              placeholder={placeholder}
              {...params}
              error={Boolean(error && touched)}
              onKeyDown={(e) => {
                e.preventDefault();
              }}
            />
          </div>
        )}
      />
      {touched && error && (
        <div className="pt-3">
          <Text variant="body1" role="alert" className="text-danger">
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
